import { extendTheme } from '@chakra-ui/react';
import { mode } from '@chakra-ui/theme-tools'; // Import mode for color mode specific styles

// Helper function to generate a basic color scale (adjust as needed for better accuracy)
// Note: For production, using a dedicated tool or pre-defined scales is often better.
const generateColorScale = (baseColor) => {
  // Basic interpolation - this is a simplification
  const lighten = (hex, percent) => adjustColor(hex, percent);
  const darken = (hex, percent) => adjustColor(hex, -percent);

  return {
    50: lighten(baseColor, 85),
    100: lighten(baseColor, 70),
    200: lighten(baseColor, 50),
    300: lighten(baseColor, 30),
    400: lighten(baseColor, 15),
    500: baseColor, // Base color
    600: darken(baseColor, 15),
    700: darken(baseColor, 30),
    800: darken(baseColor, 45),
    900: darken(baseColor, 60),
  };
};

// Helper to adjust hex color brightness (simplified)
function adjustColor(hex, percent) {
  hex = hex.replace(/^#/, '');
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  const amount = Math.round(2.55 * percent);

  r = Math.max(0, Math.min(255, r + amount));
  g = Math.max(0, Math.min(255, g + amount));
  b = Math.max(0, Math.min(255, b + amount));

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// Explorer's Journal theme based on visual_redesign_spec.md (v3)
const themeConfig = {
  initialColorMode: 'light',
  useSystemColorMode: true, // Respect user's system preference

  // Breakpoints from spec (8.3)
  breakpoints: {
    sm: '30em',    // 480px
    md: '48em',    // 768px
    lg: '62em',    // 992px
    xl: '80em',    // 1280px
    '2xl': '96em', // 1536px
  },

  // Spacing scale from spec (8.2)
  space: {
    px: '1px',
    0.5: '0.125rem',  // 2px
    1: '0.25rem',     // 4px
    1.5: '0.375rem',  // 6px
    2: '0.5rem',      // 8px
    2.5: '0.625rem',  // 10px
    3: '0.75rem',     // 12px
    3.5: '0.875rem',  // 14px
    4: '1rem',        // 16px - Base unit
    5: '1.25rem',     // 20px
    6: '1.5rem',      // 24px
    8: '2rem',        // 32px
    10: '2.5rem',     // 40px
    12: '3rem',       // 48px
    16: '4rem',       // 64px
    20: '5rem',       // 80px
    24: '6rem',       // 96px
  },

  // Semantic Tokens directly from spec (3.1, 3.2, 3.3, 3.4)
  semanticTokens: {
    colors: {
      // Backgrounds
      'bg.default': { default: '#F0E6D2', _dark: '#2C231B' },    // Aged Parchment / Worn Leather
      'bg.surface': { default: '#E6DAC0', _dark: '#38291E' },    // Faded Paper / Dark Parchment
      'bg.accent': { default: '#D9CAA7', _dark: '#483123' },     // Subtle Tea Stain / Deep Leather

      // Text
      'text.default': { default: '#3B2E1E', _dark: '#E6D7B8' },  // Aged Ink / Candlelight Parchment
      'text.muted': { default: '#6B573B', _dark: '#B7A58B' },    // Faded Ink / Faded Journal
      'text.link': { default: '#6A4E33', _dark: '#D6AD60' },     //  Brown / Lantern Gold - Changed comment to clarify gold in dark mode

      // Borders
      'border.default': { default: '#9C826B', _dark: '#6A4E33' }, // Coffee Stain / Aged Leather
      'border.heavy': { default: '#6A4E33', _dark: '#50392A' },   // Dark Leather / Dark Binding
      'border.light': { default: '#C0AD89', _dark: '#7D6C54' },   // Tea Edge / Faded Edge

      // Interactive Elements (Using GOLD as Primary per user feedback)
      'interactive.primary': { default: '#C19434', _dark: '#D6AD60' }, //  Gold / Lantern Gold
      'interactive.hover': { default: adjustColor('#C19434', 10), _dark: adjustColor('#D6AD60', 10) }, // Lighter Gold
      'interactive.pressed': { default: adjustColor('#C19434', -10), _dark: adjustColor('#D6AD60', -10) }, // Darker Gold
      'accent.highlight': { default: '#C19434', _dark: '#D6AD60' },    //  Gold / Lantern Gold (Keep consistent)

      // Map Elements
      'map.land': { default: '#EBDEC2', _dark: '#38291E' },         // Sandy Parchment / Night Parchment
      'map.water': { default: '#EBDEC2', _dark: '#233947' },        // Sandy Parchment / Midnight Sea
      'map.routes': { default: '#A04B36', _dark: '#94443D' },       // Explorer Red / Compass Red
      'map.borders': { default: '#7D6C54', _dark: '#483123' },      // Mountain Brown / Dark Earth
      'map.markers': { default: '#D6AD60', _dark: '#D6AD60' },      // Artifact Gold / Treasure Gold (Same in spec)
      'map.text': { default: '#3B2E1E', _dark: '#D7D0C0' },         // Cartographer Ink / Star Chart Silver

      // Primary/Secondary Brand Colors (Using GOLD as Primary per user feedback)
      'brand.primary': { default: '#C19434', _dark: '#D6AD60' },    //  Gold / Lantern Gold
      'brand.secondary': { default: '#9C826B', _dark: '#6A4E33' },  // Coffee Stain / Aged Leather
      'accent.gold': { default: '#C19434', _dark: '#D6AD60' },      //  Gold / Lantern Gold (Same as primary)

      // Functional Colors (Spec 3.3 - Using GOLD for Info per user feedback)
      'success': { default: '#5E7F5E', _dark: '#7FA47F' },          // Natural Green
      'warning': { default: '#C78F46', _dark: '#DDA74E' },          // Amber
      'error': { default: '#A04B36', _dark: '#C25F4E' },            // Vermilion
      'info': { default: '#C19434', _dark: '#D6AD60' },             // Use Gold for Info

      // Cultural Accent Collection (Spec 3.4) - Added _dark variants for completeness
      'accent.nordic': { default: '#5A748B', _dark: '#7B95AB' }, // Keep Nordic Blue as accent
      'accent.celtic': { default: '#5E7F5E', _dark: '#7FA47F' },
      'accent.mayan': { default: '#A04B36', _dark: '#C25F4E' },
      'accent.egyptian': { default: '#D6AD60', _dark: '#E6C680' }, // Same as Gold Dark
      'accent.mesopotamian': { default: '#9F5233', _dark: '#BF7253' },
      'accent.eastAsian': { default: '#6A4673', _dark: '#8A6693' },
    },
  },

  // Raw Color Scales (Using GOLD as Primary per user feedback)
  colors: {
    brand: {
      primary: generateColorScale('#C19434'), //  Gold base
      secondary: generateColorScale('#9C826B'), // Coffee Stain base
    },
    accent: {
      gold: generateColorScale('#C19434'), //  Gold base (Same as primary)
      // Add scales for other accents if needed, e.g., nordic, celtic etc.
    },
    // Add functional color scales if needed
    success: generateColorScale('#5E7F5E'),
    warning: generateColorScale('#C78F46'),
    error: generateColorScale('#A04B36'),
    info: generateColorScale('#C19434'), // Use Gold for Info scale
  },

  // Typography from spec (4.1, 4.2)
  fonts: {
    heading: `"Lora", serif`,
    body: `"Source Serif Pro", serif`,
    mono: `"Source Code Pro", monospace`,
    accent: `"Cinzel", serif`,
    handwritten: `"Satisfy", cursive`,
  },
  fontSizes: {
    xs: "0.75rem", sm: "0.875rem", md: "1rem", lg: "1.125rem", xl: "1.25rem",
    "2xl": "1.5rem", "3xl": "1.875rem", "4xl": "2.25rem", "5xl": "3rem", "6xl": "4rem",
  },
  fontWeights: {
    hairline: 100, thin: 200, light: 300, normal: 400, medium: 500,
    semibold: 600, bold: 700, extrabold: 800, black: 900,
  },
  lineHeights: {
    normal: "normal", none: 1, shorter: 1.25, short: 1.375,
    base: 1.5, tall: 1.625, taller: "2",
  },
  letterSpacings: {
    tighter: "-0.05em", tight: "-0.025em", normal: "0",
    wide: "0.025em", wider: "0.05em", widest: "0.1em",
  },

  // Custom Text Styles from spec (4.3) - Merged with previous theme's definitions
  textStyles: {
    journal: {
      fontFamily: 'body',
      letterSpacing: 'wide', // Spec uses 'journal' variant name, applying wide spacing
      lineHeight: 'tall',
    },
    caption: {
      fontSize: 'sm',
      color: 'text.muted',
      fontStyle: 'italic',
    },
    artifact: {
      fontFamily: 'accent', // Spec uses 'artifact' variant name
      letterSpacing: 'wide',
      fontWeight: 'medium', // Added from previous theme, seems reasonable
    },
    coordinates: {
      fontFamily: 'mono',
      fontSize: 'sm',
      letterSpacing: 'tight',
      color: 'text.muted', // Added from previous theme
    },
    handwritten: {
      fontFamily: 'handwritten',
      fontSize: 'lg', // Spec uses 'handwritten' variant name
      lineHeight: 'short',
    }
  },

  // Component Style Overrides from spec (10.3)
  components: {
    Button: {
      baseStyle: {
        fontWeight: 'medium',
        borderRadius: 'md', // Consistent radius
        transition: 'all 0.2s ease-out', // Added base transition
        _focus: {
          boxShadow: 'outline', // Use Chakra's default outline for accessibility
          outlineColor: 'interactive.primary', // Theme the outline color (now Gold)
        },
      },
      variants: {
        // Primary uses interactive.primary (Gold)
        primary: (props) => ({
          bg: 'interactive.primary',
          color: mode('white', 'gray.800')(props), // Ensure contrast on gold bg
          _hover: {
            bg: 'interactive.hover',
            transform: 'translateY(-1px)',
            boxShadow: 'md',
          },
          _active: {
            bg: 'interactive.pressed',
            transform: 'translateY(0px)', // Reset transform on active
            boxShadow: 'sm',
          },
        }),
        // Secondary uses surface/accent bg
        secondary: (props) => ({ // Pass props
          bg: 'bg.surface',
          color: 'text.default',
          borderWidth: '1px',
          borderColor: 'border.default',
          _hover: {
            bg: 'bg.accent',
            borderColor: 'border.heavy',
            transform: 'translateY(-1px)',
          },
          _active: {
            bg: mode('gray.100', 'gray.700')(props), // Standard active state
            transform: 'translateY(0px)',
          },
        }),
        // Journal variant from spec (Needs props for _active)
        journal: (props) => ({
          bg: 'bg.surface',
          color: 'text.default',
          borderWidth: '1px',
          borderColor: 'border.default',
          position: 'relative',
          overflow: 'hidden', // Needed for pseudo-element
          fontFamily: 'heading', // Added from previous theme
          // Base styles for pseudo-element (always present for positioning if needed)
          _before: {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            pointerEvents: 'none',
            // Default to no texture
            bgImage: 'none',
            opacity: 0,
            transition: 'opacity 0.3s ease-in-out',
          },
          // Apply texture ONLY when textures are enabled via body class
          'body.textures-enabled &': {
             _before: {
                opacity: 0.3, // Increased opacity
                bgImage: 'var(--texture-parchment)', // Use CSS var
                bgRepeat: 'repeat',
                bgSize: '200px', // Smaller tile size
                bgBlendMode: 'multiply',
             },
          },
          // Explicitly disable texture when disabled (redundant but safe)
          'body.textures-disabled &': {
             _before: {
               bgImage: 'none',
               opacity: 0,
             },
          },
          _hover: {
            bg: 'bg.accent',
            transform: 'translateY(-1px)',
            boxShadow: 'sm',
          },
          _active: {
            transform: 'translateY(0px)',
            bg: mode('gray.100', 'gray.700')(props),
          },
        }),
        // Artifact variant from spec (Needs props for _hover, _active)
        artifact: (props) => ({
          bg: 'transparent',
          color: 'brand.primary', // Use primary (Gold)
          border: '1px solid',
          borderColor: 'brand.primary', // Use primary (Gold)
          fontFamily: 'accent',
          _hover: {
            bg: mode('rgba(193, 148, 52, 0.1)', 'rgba(214, 173, 96, 0.1)')(props), // Use mode() for light/dark alpha
            transform: 'translateY(-1px)',
          },
          _active: {
            transform: 'translateY(0px)',
            bg: mode('rgba(193, 148, 52, 0.2)', 'rgba(214, 173, 96, 0.2)')(props),
          },
        }),
        // Map variant from spec (Needs props for _active)
        map: (props) => ({
          bg: 'bg.surface',
          color: 'map.text',
          borderWidth: '1px',
          borderColor: 'map.borders',
          fontFamily: 'heading',
          fontSize: 'sm',
          _hover: {
            bg: 'bg.accent',
            borderColor: 'map.markers',
          },
          _active: {
            bg: mode('gray.100', 'gray.700')(props),
          },
        }),
        // Link style button (Needs props for _active)
        link: (props) => ({
          bg: 'transparent',
          color: 'text.link', // Uses gold in dark mode based on semantic token
          padding: 0,
          height: 'auto',
          fontWeight: 'normal',
          _hover: {
            textDecoration: 'underline',
            color: 'text.link', // Keep link color on hover, just underline
          },
          _active: {
            // Correctly use mode to get the appropriate color before adjusting
            color: mode(
              adjustColor(themeConfig.semanticTokens.colors['text.link'].default, -10), // Adjust light mode color
              adjustColor(themeConfig.semanticTokens.colors['text.link']._dark, -10) // Adjust dark mode color
            )(props),
          },
        }),
        // Ghost style button (Needs props for _hover, _active)
        ghost: (props) => ({
          bg: 'transparent',
          color: 'text.muted',
          _hover: {
            bg: mode('gray.100', 'whiteAlpha.200')(props), // Standard ghost hover
            color: 'text.default',
          },
          _active: {
            bg: mode('gray.200', 'whiteAlpha.300')(props),
          },
        }),
        // Outline style button (Needs props for _hover, _active)
        outline: (props) => ({
          bg: 'transparent',
          color: 'brand.primary', // Use primary (Gold)
          borderWidth: '1px',
          borderColor: 'brand.primary', // Use primary (Gold)
          _hover: {
            bg: mode('yellow.50', 'whiteAlpha.100')(props), // Subtle gold background on hover
            transform: 'translateY(-1px)',
          },
          _active: {
            transform: 'translateY(0px)',
            bg: mode('yellow.100', 'whiteAlpha.200')(props),
          },
        }),
      },
      defaultProps: {
        variant: 'primary', // Default to primary (now Gold)
        size: 'md',
      },
    },

    Card: { // No changes needed for Card variants themselves
      baseStyle: {
        container: {
          bg: 'bg.surface',
          borderWidth: '1px',
          borderColor: 'border.default',
          borderRadius: 'md',
          boxShadow: 'sm',
          position: 'relative',
          overflow: 'hidden', // Important for pseudo-elements
          transition: 'all 0.2s ease-out',
        },
        header: { p: 4, pb: 2 },
        body: { p: 4 },
        footer: { p: 4, pt: 2 },
      },
      variants: {
        // Reverted journal variant back to a static object
        journal: {
          container: {
            // Base styles for pseudo-element
            _before: {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 0,
              pointerEvents: 'none',
              // Default to no texture
              bgImage: 'none',
              opacity: 0,
              transition: 'opacity 0.3s ease-in-out',
            },
            // Apply texture ONLY when textures are enabled via body class
            // Apply texture ONLY when textures are enabled via body class
            // Reverted to original --texture-aged
            'body.textures-enabled &': {
               _before: {
                  opacity: 0.25,
                  bgImage: 'var(--texture-aged)', // Reverted to original
                  bgRepeat: 'repeat',
                  bgSize: '200px',
                  bgBlendMode: 'multiply',
               },
            },
            // Explicitly disable texture when disabled
            'body.textures-disabled &': {
               _before: {
                 bgImage: 'none',
                 opacity: 0,
               },
             },
           }, // Correctly close the container object within journal
        }, // Correctly close the journal object
        map: {
          container: {
            borderColor: 'map.borders',
            bg: 'map.land', // Use map land color
            // Base styles for pseudo-element
            _before: {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 0,
              pointerEvents: 'none',
              // Default to no texture
              bgImage: 'none',
              opacity: 0,
              transition: 'opacity 0.3s ease-in-out',
            },
            // Apply texture ONLY when textures are enabled via body class
            'body.textures-enabled &': {
               _before: {
                  // Canvas texture for map cards
                  opacity: 0.1,
                  bgImage: 'var(--texture-canvas)',
                  bgRepeat: 'repeat',
                  bgSize: '200px',
                  bgBlendMode: 'multiply',
               },
            },
            // Explicitly disable texture when disabled
            'body.textures-disabled &': {
               _before: {
                 bgImage: 'none',
                 opacity: 0,
               },
            },
          },
        },
        artifact: {
          container: {
            borderColor: 'brand.primary', // Use primary (Gold)
            // Use boxShadow to create the border effect without affecting layout
            boxShadow: `0 0 0 1px var(--chakra-colors-brand-primary), var(--chakra-shadows-sm)`,
          },
        },
      },
      defaultProps: {
        variant: 'journal', // Default to journal style
      },
    },

    Select: {
      variants: {
        outline: {
          field: {
            bg: 'bg.surface',
            borderColor: 'border.default',
            _hover: {
              borderColor: 'border.heavy',
            },
            _focus: {
              borderColor: 'interactive.primary',
              boxShadow: `0 0 0 1px var(--chakra-colors-interactive-primary)`,
            },
          },
        },
      },
    },
    Input: { // Needs props for variants
      // Includes Textarea, Select potentially
      variants: {
        outline: (props) => ({
          // Default variant
          field: {
            bg: 'bg.surface',
            border: '1px solid',
            borderColor: 'border.default',
            borderRadius: 'md',
            _hover: {
              borderColor: 'border.heavy',
            },
            _focus: {
              borderColor: 'interactive.primary', // Gold focus
              boxShadow: `0 0 0 1px var(--chakra-colors-interactive-primary)`,
              zIndex: 1, // Ensure focus ring is on top
            },
            _placeholder: {
              color: 'text.muted',
            },
          },
        }),
        journal: (props) => ({
          field: {
            bg: 'bg.surface',
            border: '1px solid',
            borderColor: 'border.default',
            borderRadius: 'sm', // Slightly less rounded for journal feel
            fontFamily: 'body',
            position: 'relative',
            overflow: 'hidden',
            // Base styles for pseudo-element
            _before: {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 0,
              pointerEvents: 'none',
              // Default to no texture
              bgImage: 'none',
              opacity: 0,
              transition: 'opacity 0.3s ease-in-out',
            },
            // Apply texture ONLY when textures are enabled via body class
            'body.textures-enabled &': {
               _before: {
                  opacity: 0.2, // Increased opacity
                  bgImage: 'var(--texture-parchment)',
                  bgRepeat: 'repeat',
                  bgSize: '200px',
                  bgBlendMode: 'multiply',
               },
            },
            // Explicitly disable texture when disabled
            'body.textures-disabled &': {
               _before: {
                 bgImage: 'none',
                 opacity: 0,
               },
            },
            _hover: {
              borderColor: 'border.heavy',
            },
            _focus: {
              borderColor: 'interactive.primary', // Gold focus
              boxShadow: `0 0 0 1px var(--chakra-colors-interactive-primary)`,
              zIndex: 1,
            },
            _placeholder: {
              color: 'text.muted',
            },
          },
        }),
        map: (props) => ({
          // Added map variant from previous theme
          field: {
            bg: 'bg.surface',
            border: '1px solid',
            borderColor: 'map.borders',
            fontFamily: 'body',
            // Explicit light mode styles for better contrast
            _light: {
              color: 'ancient.mapText', // Ensure input text is dark enough
              _placeholder: {
                color: 'ancient.mapText', // Ensure placeholder is dark enough
              },
            },
            // Dark mode uses default text.default which should be light
            _dark: {
              color: 'text.default', // Explicitly set for clarity if needed
              _placeholder: {
                color: 'text.muted', // Keep muted placeholder for dark mode
              },
            },
            _focus: {
              borderColor: 'map.markers', // Gold focus for map inputs
              boxShadow: '0 0 0 1px var(--chakra-colors-map-markers)',
              zIndex: 1,
            },
          },
        }),
      },
      defaultProps: {
        variant: 'outline', // Default to standard outline
      },
    },

    // Adding other components from spec and previous theme
    Stat: { // No changes needed
      baseStyle: {
        label: { color: 'text.muted', fontSize: 'sm', fontWeight: 'medium' },
        number: { color: 'text.default', fontWeight: 'semibold', fontFamily: 'heading' },
        helpText: { color: 'text.muted', fontSize: 'xs' },
        icon: { color: 'interactive.primary' }, // Gold icon
      },
      variants: {
        journal: {
          container: { padding: 3 },
          label: { fontFamily: 'heading', fontSize: 'xs', letterSpacing: 'wide', textTransform: 'uppercase' },
          number: { fontSize: 'xl', color: 'interactive.primary' }, // Gold number
        },
      },
    },

    Link: {
      baseStyle: {
        color: 'text.link', // Uses gold in dark mode based on semantic token
        transition: 'color 0.2s ease-out',
        _hover: {
          textDecoration: 'underline',
          color: 'text.link', // Keep link color on hover
        },
      },
      variants: {
        // Journal link from spec (Use Gold for underline)
        journal: {
          fontFamily: 'heading',
          fontWeight: 'medium',
          position: 'relative',
          textDecoration: 'none', // Remove default underline
          color: 'text.default', // Use default text color for journal links
          _hover: {
            textDecoration: 'none', // Ensure no underline on hover
            color: 'brand.primary', // Hover to primary gold
            _after: { width: '100%' }, // Animate underline
          },
          _after: {
            // Underline animation
            content: '""',
            position: 'absolute',
            left: 0,
            bottom: '-2px', // Position slightly below text
            height: '1px',
            width: '0',
            bg: 'brand.primary', // Gold underline
            transition: 'width 0.3s ease',
          },
        },
        // Artifact link from spec (Uses Gold)
        artifact: {
          color: 'brand.primary', // Use primary (Gold)
          fontFamily: 'accent',
          textDecoration: 'none',
          _hover: {
            color: 'interactive.hover', // Use lighter gold on hover
            textDecoration: 'none',
          },
        },
      },
    },

    Heading: { // No changes needed for Heading variants
      baseStyle: {
        fontFamily: 'heading',
        color: 'text.default',
        fontWeight: 'medium', // Default weight from spec
        letterSpacing: 'tight',
        lineHeight: 'shorter',
      },
      variants: {
        // Journal heading from spec
        journal: {
          position: 'relative',
          _after: {
            // Decorative underline
            content: '""',
            display: 'block',
            width: '60px',
            height: '2px',
            mt: 2,
            bg: 'border.default', // Use semantic token
          },
        },
        // Artifact heading from spec (Uses Gold)
        artifact: {
          fontFamily: 'accent',
          color: 'brand.primary', // Use primary (Gold)
          fontWeight: 'normal', // Spec override
          letterSpacing: 'wide',
        },
        // Map title from spec (No change needed)
        mapTitle: {
          color: 'map.text', // Use semantic token
          letterSpacing: 'wider',
          textTransform: 'uppercase',
          fontSize: 'lg',
          mb: 4,
        },
      },
    },

    Text: {
      // Inherits from global, add variants (No changes needed)
      variants: {
        journal: { fontFamily: 'body', lineHeight: 'tall', fontSize: 'md' },
        caption: { fontSize: 'sm', color: 'text.muted', fontStyle: 'italic' },
        handwritten: { fontFamily: 'handwritten', lineHeight: 'short', fontSize: 'lg' },
        coordinates: { fontFamily: 'mono', fontSize: 'sm', color: 'text.muted', letterSpacing: 'tight' },
      },
    },

    Badge: { // Update solid, outline, and artifact variants (Needs props)
      baseStyle: {
        fontFamily: 'body', // Use body font for readability
        fontWeight: 'medium',
        px: 2,
        py: 0.5,
        borderRadius: 'md',
        textTransform: 'uppercase', // Default to uppercase
        fontSize: 'xs', // Smaller default size
        letterSpacing: 'wider', // Default spacing
      },
      variants: {
        // Subtle uses accent background (Needs props)
        subtle: (props) => ({
          bg: mode('blackAlpha.100', 'whiteAlpha.100')(props), // Standard subtle
          color: mode('blackAlpha.800', 'whiteAlpha.800')(props),
          fontWeight: 'normal',
          textTransform: 'none',
          letterSpacing: 'normal',
        }),
        // Solid uses primary (Gold) (Needs props)
        solid: (props) => ({
          bg: 'brand.primary', // Use primary (Gold)
          color: mode('white', 'gray.800')(props), // Ensure contrast
        }),
        // Outline uses primary (Gold) (Needs props)
        outline: (props) => ({
          color: 'brand.primary', // Use primary (Gold)
          // Use yellow scale for boxShadow color hint if available, otherwise fallback
          boxShadow: `inset 0 0 0px 1px ${mode(theme?.colors?.yellow?.[500] || themeConfig.semanticTokens.colors['brand.primary'].default, theme?.colors?.yellow?.[200] || themeConfig.semanticTokens.colors['brand.primary']._dark)(props)}`,
        }),
        // Journal variant from spec (No change needed)
        journal: {
          bg: 'bg.surface',
          color: 'text.default',
          border: '1px solid',
          borderColor: 'border.default',
          fontFamily: 'heading',
          // fontSize: 'xs', // Inherited
          // textTransform: 'uppercase', // Inherited
          // letterSpacing: 'wider', // Inherited
        },
        // Artifact variant from spec (Uses Gold) (Needs props)
        artifact: (props) => ({
          bg: 'brand.primary', // Use primary (Gold)
          color: mode('white', 'gray.800')(props), // Ensure contrast
        }),
        // Map variant from spec (Needs props)
        map: (props) => ({
          bg: 'map.markers',
          color: mode('#3B2E1E', 'white')(props), // Ensure contrast on marker bg
          fontFamily: 'heading',
          textTransform: 'none', // Override default
          letterSpacing: 'wide', // Override default
        }),
        // Cultural accents from spec (No change needed)
        nordic: { bg: 'accent.nordic', color: 'white' },
        celtic: { bg: 'accent.celtic', color: 'white' },
        mayan: { bg: 'accent.mayan', color: 'white' },
        egyptian: { bg: 'accent.egyptian', color: '#3B2E1E' }, // Dark text on gold
        mesopotamian: { bg: 'accent.mesopotamian', color: 'white' },
        eastAsian: { bg: 'accent.eastAsian', color: 'white' },
      },
      defaultProps: {
        variant: 'subtle', // Default to subtle
      },
    },

    Divider: {
      baseStyle: {
        borderColor: 'border.light', // Use semantic token
        opacity: 0.6, // Slightly less opaque than previous
        my: 4, // Default margin
      },
      variants: {
        // Journal divider using map-divider texture
        journal: {
          border: 'none', // Remove default border
          height: '40px', // Increased height for the texture
          // Default to no background image
          backgroundImage: 'none',
          opacity: 0, // Default to hidden if no texture
          transition: 'opacity 0.3s ease-in-out, background-image 0s 0.3s', // Delay hiding image until fade out
          my: 8, // Adjust vertical margin

          // Apply texture ONLY when textures are enabled via body class
          'body.textures-enabled &': {
            backgroundImage: "url('/textures/wallrepeating.png')", // New image
            backgroundRepeat: 'repeat-x', // Repeat horizontally
            backgroundPosition: 'center',
            backgroundSize: 'auto 100%', // Tile height 100% of element height
            opacity: 0.6, // Adjust opacity as needed (can be 1 if it's a solid border look)
            transition: 'opacity 0.3s ease-in-out, background-image 0s 0s', // Apply immediately
          },
          // Explicitly disable texture when disabled
          'body.textures-disabled &': {
            backgroundImage: 'none',
            opacity: 0,
          },
        },
        // NEW Dashboard divider using map-divider texture
        dashboardDivider: {
          border: 'none', // Remove default border
          height: '20px', // Height for the texture
          backgroundImage: 'none', // Default to no texture
          opacity: 0, // Default to hidden
          transition: 'opacity 0.3s ease-in-out, background-image 0s 0.3s',
          my: 6, // Adjust vertical margin for dashboard context

          // Apply texture ONLY when textures are enabled via body class
          'body.textures-enabled &': {
            backgroundImage: 'var(--texture-divider)', // Use the specific dashboard divider texture variable
            backgroundRepeat: 'repeat-x',
            backgroundPosition: 'center',
            backgroundSize: 'contain',
            opacity: 0.5, // Slightly more subtle opacity for dashboard
            transition: 'opacity 0.3s ease-in-out, background-image 0s 0s',
          },
          // Explicitly disable texture when disabled
          'body.textures-disabled &': {
            backgroundImage: 'none',
            opacity: 0,
          },
        },
        // Map divider from spec
        map: {
          borderStyle: 'dashed',
          borderWidth: '1px',
          borderColor: 'map.borders', // Use map border color
          opacity: 1, // Ensure dashed line is visible
        },
      },
    },

    // Add Box component styles for dashboardSection variant
    Box: {
      variants: {
        dashboardSection: {
          bg: 'bg.surface',
          borderWidth: '1px',
          borderColor: 'border.default',
          borderRadius: 'md',
          boxShadow: 'md',
          p: { base: 4, md: 6 }, // Default padding for sections
          position: 'relative',
          overflow: 'hidden', // Needed for pseudo-element
          // Base styles for pseudo-element
          _before: {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            pointerEvents: 'none',
            // Default to no texture
            bgImage: 'none',
            opacity: 0,
            transition: 'opacity 0.3s ease-in-out',
          },
          // Apply texture ONLY when textures are enabled via body class
          'body.textures-enabled &': {
             _before: {
                opacity: 0.15, // Opacity for card background texture
                bgImage: 'var(--texture-card-bg)', // Use canvas texture variable
                bgRepeat: 'repeat',
                bgSize: '200px', // Adjust size as needed
                bgBlendMode: 'multiply',
             },
          },
          // Explicitly disable texture when disabled
          'body.textures-disabled &': {
             _before: {
               bgImage: 'none',
               opacity: 0,
             },
           },
        },
      },
    },

    Container: {
      // Added from previous theme, seems useful
      baseStyle: {
        w: '100%',
        mx: 'auto',
        maxW: 'container.xl', // Default max width
        px: { base: 4, md: 6 }, // Default padding
      },
      variants: {
        // Journal container from spec
        journal: {
          position: 'relative',
          px: { base: 4, md: 8 }, // Spec padding
          py: { base: 6, md: 10 }, // Spec padding
          bg: 'bg.surface', // Add surface background
          boxShadow: 'md', // Add shadow
          borderRadius: 'md', // Add rounding
          _after: {
            // Decorative bottom line from spec
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '10%',
            right: '10%',
            height: '1px',
            bgGradient: 'linear(to-r, transparent, border.default, transparent)',
            opacity: 0.6,
          },
        },
      },
    },

    // Add Alert styling for toasts/notifications (Spec 11.7 - Update info color) (Needs props)
    Alert: {
      baseStyle: {
        container: {
          borderRadius: 'md',
          boxShadow: 'md',
        },
      },
      variants: {
        // Journal variant for toasts
        journal: (props) => ({
          container: {
            bg: 'bg.surface',
            color: 'text.default',
            borderWidth: '1px',
            borderColor: 'border.default',
            position: 'relative',
            overflow: 'hidden',
            // Base styles for pseudo-element
            _before: {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 0,
              pointerEvents: 'none',
              // Default to no texture
              bgImage: 'none',
              opacity: 0,
              transition: 'opacity 0.3s ease-in-out',
            },
            // Apply texture ONLY when textures are enabled via body class
            'body.textures-enabled &': {
               _before: {
                  // Texture
                  opacity: 0.08,
                  bgImage: 'var(--texture-aged)',
                  bgRepeat: 'repeat',
                  bgSize: '200px',
                  bgBlendMode: 'multiply',
               },
            },
            // Explicitly disable texture when disabled
            'body.textures-disabled &': {
               _before: {
                 bgImage: 'none',
                 opacity: 0,
               },
            },
            // Color based on status (Info now uses Gold)
            // Configure border style
            borderLeftWidth: '4px', // Accent border on the left
            borderLeftColor:
              {
                info: 'info',
                warning: 'warning',
                success: 'success',
                error: 'error',
              }[props.status] || 'border.default', // Fallback
          },
          title: { fontFamily: 'heading' },
          description: { fontFamily: 'body' },
          icon: {
            color:
              {
                // Match icon color to status (Info now uses Gold)
                info: 'info',
                warning: 'warning',
                success: 'success',
                error: 'error',
              }[props.status] || 'text.default', // Fallback
          },
        }),
      },
    },

    // Add Drawer component styling to ensure correct colors in dark mode
    Drawer: {
      baseStyle: {
        overlay: {
          bg: 'blackAlpha.800', // Ensure dark overlay with no blue tint
        },
        dialog: {
          bg: 'bg.surface', // Use our semantic token for surface
          color: 'text.default', // Use our semantic token for text
          borderColor: 'border.default', // Use our semantic token for borders
        },
        header: {
          borderColor: 'border.default', // Use our semantic token for borders
        },
        body: {
          bg: 'bg.surface', // Use our semantic token for surface
        },
        footer: {
          borderColor: 'border.default', // Use our semantic token for borders
        },
      },
    },

    // Add other component styles from spec as needed (e.g., Tabs, Accordion, Table, List)
  },

  // Global Styles from spec (10.2)
  styles: {
    global: (props) => ({
      // CSS Variables for Textures (Spec 5.1, 5.3) - Corrected paths
      ':root': {
        '--texture-parchment': 'url("/textures/parchment.png")',
        '--texture-leather': 'url("/textures/leather.png")', // Corrected typo
        '--texture-canvas': 'url("/textures/canvas.png")',
        '--texture-linen': 'url("/textures/linen.png")',
        '--texture-aged': 'url("/textures/aged-paper.png")',
        // Add others from spec if available in /textures
        '--texture-corner-flourish': 'url("/textures/flourish.png")',
        '--texture-compass-rose': 'url("/textures/compass-rose.png")',
        '--texture-map-divider': 'url("/textures/map-divider.png")',
        '--texture-map-cartouche': 'url("/textures/map-cartouche.png")',
        '--texture-celtic-motif': 'url("/textures/celtic-motif.png")',
        '--texture-egyptian-motif': 'url("/textures/egyptian-motif.png")',
        // Site Card Textures
        '--texture-sitecard-light': 'url("/textures/sitecards/sitecardlight.png")',
        '--texture-sitecard-dark': 'url("/textures/sitecards/sitecarddark.png")',
        // HowItWorks Section Textures (Added)
        '--texture-field-guide-light': 'url("/textures/textures/fieldguidetexturelight.png")',
        '--texture-field-guide-dark': 'url("/textures/textures/fieldguidetexturedark.png")',
        '--texture-woven-note-light': 'url("/textures/textures/wovenlight.png")',
        '--texture-woven-note-dark': 'url("/textures/textures/wovendark.png")',
      },

      // Base body styles (Spec 10.2)
      'html, body': {
        bg: 'bg.default', // Use semantic token
        color: 'text.default', // Use semantic token
        fontFamily: 'body',
        lineHeight: 'base',
        transitionProperty: 'background-color', // Smooth color mode transition
        transitionDuration: 'normal',
        // Base styles for the pseudo-element (always present for positioning)
        _before: {
          content: '""',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bg: 'bg.default', // Match background color (keeps bg consistent)
          zIndex: -1,
          pointerEvents: 'none',
          // Default to no texture
          backgroundImage: 'none',
          opacity: 0,
          transition: 'opacity 0.3s ease-in-out', // Add transition for smoothness
        },
      },

      // Apply texture ONLY when textures are enabled
      'body.textures-enabled html, body.textures-enabled body': {
        _before: {
          backgroundImage: mode('var(--texture-parchment)', 'var(--texture-leather)')(props),
          backgroundRepeat: 'repeat',
          backgroundSize: '300px',
          backgroundAttachment: 'fixed',
          backgroundBlendMode: mode('soft-light', 'multiply')(props),
          opacity: 0.1, // Reduced opacity for global texture (Spec: 0.08-0.15)
        },
      },

      // Explicitly ensure no texture when disabled (redundant but safe)
      'body.textures-disabled html, body.textures-disabled body': {
         _before: {
           backgroundImage: 'none',
           opacity: 0,
         },
      },


      // Headings (Spec 10.2)
      'h1, h2, h3, h4, h5, h6': {
        fontFamily: 'heading',
        color: 'text.default', // Ensure headings use default text color
        letterSpacing: 'tight',
        // Default margins can be added here if needed
      },

      // Links (Spec 10.2) - Base link style using Gold in dark mode
      a: {
        color: 'text.link', // Uses gold in dark mode based on semantic token
        textDecoration: 'none', // Remove default underline globally
        transition: 'color 0.2s ease-out',
        _hover: {
          textDecoration: 'underline', // Add underline on hover
          color: 'text.link', // Keep link color on hover
        },
      },

      // Focus outline (Spec 10.2 - Use Gold)
      ':focus-visible': {
        outline: '2px solid',
        outlineColor: 'interactive.primary', // Use primary (Gold) for focus
        outlineOffset: '2px',
        boxShadow: 'none', // Remove default browser focus shadow if needed
      },

      // Scrollbar styling (Spec 10.2)
      '::-webkit-scrollbar': {
        width: '10px', // Slightly wider scrollbar
        height: '10px',
      },
      '::-webkit-scrollbar-track': {
        // Use subtle background based on mode
        bg: mode('rgba(156, 130, 107, 0.1)', 'rgba(106, 78, 51, 0.1)')(props),
      },
      '::-webkit-scrollbar-thumb': {
        // Use border color for thumb, slightly more opaque
        bg: mode('rgba(156, 130, 107, 0.6)', 'rgba(106, 78, 51, 0.6)')(props),
        borderRadius: 'full',
        border: `2px solid ${mode('#F0E6D2', '#2C231B')(props)}`, // Add border matching bg
      },
      '::-webkit-scrollbar-thumb:hover': {
        bg: mode('rgba(156, 130, 107, 0.8)', 'rgba(106, 78, 51, 0.8)')(props),
      },

      // Selection styling (Spec 10.2 - Uses Gold)
      '::selection': {
        // Use accent gold with opacity
        bg: mode('rgba(193, 148, 52, 0.3)', 'rgba(214, 173, 96, 0.3)')(props),
        color: 'inherit', // Keep original text color
      },

      // Image drag prevention (Spec 10.2)
      img: {
        userDrag: 'none',
        WebkitUserDrag: 'none',
        // Add display: block to prevent extra space below images
        display: 'block',
        maxWidth: '100%', // Ensure images are responsive by default
        height: 'auto',
      },

      // Add other global styles or utility classes from spec if needed
      // e.g., .journal-texture, .map-texture, .decorated-corners from previous theme
      // These might be better applied via component styles or Chakra's style props
    }),
  },
};

// Create the final theme object
const theme = extendTheme(themeConfig);

export default theme;
